import {
  TextField,
  InputAdornment,
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';
import React, { ReactElement, useEffect, useContext, useMemo, useState } from 'react';
import { Search as SearchIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { gray40, gray100, white, gray60, red50, gray80 } from '../../../constants/colors';
import { WatchSearchItemsType } from '../../../types/result';
import { AuthContext } from '../../../contexts/auth';
import { getUsersByCompanyId } from '../../../api/firebase/firestore/user/read';
import {
  needsReviewFilterLabel,
  needsReviewFilterType,
  SUB_PATTERN_ID_BASE,
  ocrStatus,
  registerStatus,
} from '../../../constants/enum';
import { assigneeLabels } from '../../../constants/assignee';
import { useCompanyId } from '../../../hooks/useCompanyId';
import {
  SearchFieldConfig,
  searchFieldInputType,
  searchMatchMode,
} from '../../../types/searchCustomize';
import {
  OcrItemKeywordValue,
  OcrItemKeywords,
  toKeywordList,
  toKeywordText,
} from '../../../utils/search/ocrItemKeywords';
import { resolveChoiceValue } from '../../../utils/pattern/choiceUtils';
import type { SubPatternType } from '../../../types/subPattern';
import { subPatternLabel } from '../../../constants/subPattern';

dayjs.extend(customParseFormat);

/** OCR読取項目のうち date 型のフィールドのデフォルト表示フォーマット */
const DEFAULT_DATE_FORMAT = 'YYYY/MM/DD';

// 読取ステータスのラベルマッピング
const ocrStatusLabels: { [key: number]: string } = {
  [ocrStatus.processing]: '読取中',
  [ocrStatus.completed]: '読取完了',
  [ocrStatus.error]: '読取エラー',
};

interface SearchFormProps {
  watchSearchItems: WatchSearchItemsType;
  setWatchSearchItems: React.Dispatch<React.SetStateAction<WatchSearchItemsType>>;
  onClearSearch?: () => void;
  customizeFields?: SearchFieldConfig[];
  ocrItemKeywords?: OcrItemKeywords;
  onOcrItemKeywordChange?: (fieldName: string, value: OcrItemKeywordValue) => void;
  /** 作成者フィールドを非表示にする（フリーOCRでは作成者検索を出さない） */
  hideCreatedBy?: boolean;
  /**
   * 要注意の絞り込みを非表示にする（#3334）。
   * 要注意は未処理のものだけが対象のため、確認済・出力済タブでは常に0件になる
   */
  hideNeedsReview?: boolean;
  /**
   * 子パターンの絞り込み候補（#2967）。有効な子パターンのみ。
   * 未指定・空なら絞り込み自体を表示しない
   */
  subPatterns?: SubPatternType[];
}

const AiOcrSearchForm = (props: SearchFormProps): ReactElement => {
  const {
    watchSearchItems,
    setWatchSearchItems,
    onClearSearch,
    customizeFields,
    ocrItemKeywords,
    onOcrItemKeywordChange,
    hideCreatedBy,
    hideNeedsReview,
    subPatterns,
  } = props;
  const { user, isLoading } = useContext(AuthContext);
  const { companyId: effectiveCompanyId } = useCompanyId();

  // カスタマイズ設定による表示制御
  const isFieldVisible = (fieldName: string): boolean => {
    if (!customizeFields) return true;
    const field = customizeFields.find(f => f.fieldName === fieldName);
    return field?.enabled ?? true;
  };

  // 有効なOCR読取項目を取得
  const enabledOcrFields = useMemo(
    () => (customizeFields ?? []).filter(f => f.isOcrItem && f.enabled),
    [customizeFields],
  );

  // 日付バリデーションエラー
  const dateError = useMemo(() => {
    if (watchSearchItems.startDate && watchSearchItems.endDate) {
      if (watchSearchItems.startDate.isAfter(watchSearchItems.endDate)) {
        return '開始日は終了日より前の日付を選択してください';
      }
    }
    return null;
  }, [watchSearchItems.startDate, watchSearchItems.endDate]);

  const inputBaseStyle = {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: gray60,
      borderWidth: '1px',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: gray100,
    },
  };

  const styles = {
    searchFormBox: {
      gap: 2,
      padding: 2,
      border: `1px solid ${gray40}`,
      borderRadius: 1,
      backgroundColor: white,
    },
    searchForms: {
      flexDirection: 'column',
    },
    searchFromSortOrder: {
      display: 'flex',
      flexDirection: 'row',
      gap: 2,
      flexWrap: 'wrap',
      justifyContent: 'flex-start',
    },
    formWidth: {
      minWidth: '340px',
      textAlign: 'left',
      '@media screen and (max-width:960px)': {
        minWidth: '100%',
      },
      ...inputBaseStyle,
    },
    datePickerRootBox: {
      '@media screen and (max-width:960px)': {
        width: '100%',
      },
    },
    datePickerBox: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    },
    datePickerWidth: {
      width: '154px',
      ...inputBaseStyle,
      '@media screen and (max-width: 960px)': {
        width: '100%',
      },
    },
    selectFiled: {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: gray60,
        borderWidth: '1px',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: gray100,
        borderWidth: '1px',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: gray100,
        borderWidth: '1px',
      },
      minWidth: '340px',
      textAlign: 'left',
      '@media screen and (max-width:960px)': {
        minWidth: '100%',
      },
    },
    clearButton: {
      '&:hover': {
        backgroundColor: white,
        border: `1px solid ${gray60}`,
        boxShadow: 'none',
        color: gray60,
        opacity: '80%',
      },
      backgroundColor: white,
      border: `1px solid ${gray80}`,
      boxShadow: 'none',
      color: gray80,
      height: '40px',
      alignSelf: 'flex-end', // 右下に配置
      marginLeft: 'auto', // 右端に寄せる
    },
  };

  // 検索用作成者リスト
  interface UserListItem {
    value: string;
    label: string;
  }
  const [userList, setUserList] = useState<Array<UserListItem>>([]);

  useEffect(() => {
    // 認証チェック: ユーザーが存在し、認証ロード中でない場合のみ実行
    if (isLoading || !user) {
      return;
    }

    void (async (): Promise<void> => {
      try {
        // 同じ会社のユーザーのみ取得（代理モード時は対象企業ID）
        const companyId = effectiveCompanyId || user.company_id;
        if (!companyId) {
          setUserList([]);
          return;
        }
        const users = await getUsersByCompanyId(companyId);
        const userList = users
          .filter(user => user.register_status === registerStatus.finished)
          .map(user => ({
            value: user.uid as string,
            label: user.name as string,
          }));
        setUserList(userList);
      } catch (error) {
        // エラーが発生した場合は空のリストを設定
        setUserList([]);
      }
    })();
  }, [user, isLoading, effectiveCompanyId]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={styles.searchFormBox}>
        <Box sx={styles.searchForms}>
          <Box sx={styles.searchFromSortOrder}>
            {/* フリーワード検索 */}
            {isFieldVisible('search_text') && (
              <TextField
                value={watchSearchItems.freeWord ?? ''}
                sx={styles.formWidth}
                onChange={(e): void =>
                  setWatchSearchItems({ ...watchSearchItems, freeWord: e.target.value || null })
                }
                placeholder="フリーワード検索"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
                size="small"
              />
            )}

            {/* 担当者（#2642）- デフォルト検索軸。#2969: 作成者より先に配置 */}
            {isFieldVisible('assignee') && (
              <Select
                displayEmpty
                sx={styles.selectFiled}
                value={watchSearchItems.assignee ?? ''}
                onChange={(e): void =>
                  setWatchSearchItems({ ...watchSearchItems, assignee: e.target.value || null })
                }
                size="small"
                renderValue={(selected: string): ReactElement | undefined => {
                  const selectedUser = userList.find(user => user.value === selected);
                  if (selected === '') {
                    return (
                      <span style={{ color: gray60 }}>{assigneeLabels.selectPlaceholder}</span>
                    );
                  } else {
                    return <span>{selectedUser?.label}</span>;
                  }
                }}
              >
                <MenuItem value="">{assigneeLabels.selectPlaceholder}</MenuItem>
                {userList.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            )}

            {/* 作成者（#2969: 担当者の右隣に配置・デフォルト非表示）。hideCreatedBy 指定時は非表示（フリーOCR） */}
            {!hideCreatedBy && isFieldVisible('created_by') && (
              <Select
                displayEmpty
                sx={styles.selectFiled}
                value={watchSearchItems.responsiblePerson ?? ''}
                onChange={(e): void =>
                  setWatchSearchItems({ ...watchSearchItems, responsiblePerson: e.target.value })
                }
                size="small"
                renderValue={(selected: string): ReactElement | undefined => {
                  const selectedUser = userList.find(user => user.value === selected);
                  if (selected === '') {
                    return <span style={{ color: gray60 }}>作成者を選択</span>;
                  } else {
                    return <span>{selectedUser?.label}</span>;
                  }
                }}
              >
                {/* プレースホルダーとして表示 */}
                <MenuItem value="">作成者を選択</MenuItem>
                {userList.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            )}

            {/* 読取ステータス */}
            {isFieldVisible('ocr_status') && (
              <Select
                displayEmpty
                sx={styles.selectFiled}
                value={watchSearchItems.ocrStatus ?? ''}
                onChange={(e): void => {
                  const value = e.target.value;
                  setWatchSearchItems({
                    ...watchSearchItems,
                    ocrStatus: value === '' ? null : Number(value),
                  });
                }}
                size="small"
                renderValue={(selected: string | number): ReactElement | undefined => {
                  if (selected === '') {
                    return <span style={{ color: gray60 }}>読取ステータスを選択</span>;
                  }
                  return <span>{ocrStatusLabels[Number(selected)]}</span>;
                }}
              >
                {/* プレースホルダー */}
                <MenuItem value="">読取ステータスを選択</MenuItem>
                {/* ラベルが定義されているステータスのみ表示 */}
                {Object.entries(ocrStatus)
                  .filter(([, value]) => ocrStatusLabels[value])
                  .map(([key, value]) => (
                    <MenuItem key={key} value={value}>
                      {ocrStatusLabels[value]}
                    </MenuItem>
                  ))}
              </Select>
            )}

            {/* 要注意の絞り込み（#3262）。読取ステータスと同じ見た目で揃える。
                「要注意以外」は一括で確認済にする運用のために用意している。
                hideNeedsReview 指定時は非表示（未処理タブ以外・フリーOCR）#3334 */}
            {!hideNeedsReview && isFieldVisible('needs_review') && (
              <Select
                displayEmpty
                sx={styles.selectFiled}
                value={watchSearchItems.needsReview ?? ''}
                onChange={(e): void => {
                  const value = e.target.value;
                  setWatchSearchItems({
                    ...watchSearchItems,
                    // 「すべて」は条件なしとして undefined に揃える
                    needsReview: value === '' ? undefined : Number(value),
                  });
                }}
                size="small"
                renderValue={(selected: string | number): ReactElement | undefined => {
                  if (selected === '') {
                    return <span style={{ color: gray60 }}>要注意で絞り込む</span>;
                  }
                  return <span>{needsReviewFilterLabel[Number(selected)]}</span>;
                }}
              >
                <MenuItem value="">すべて</MenuItem>
                <MenuItem value={needsReviewFilterType.only}>
                  {needsReviewFilterLabel[needsReviewFilterType.only]}
                </MenuItem>
                <MenuItem value={needsReviewFilterType.exclude}>
                  {needsReviewFilterLabel[needsReviewFilterType.exclude]}
                </MenuItem>
              </Select>
            )}

            {/* 子パターンの絞り込み（#2967）。要注意の隣に置き、同じ見た目で揃える。
                子パターンが1件も登録されていないパターンでは表示しない */}
            {subPatterns && subPatterns.length > 0 && (
              <Select
                displayEmpty
                sx={styles.selectFiled}
                value={watchSearchItems.subPatternId ?? ''}
                onChange={(e): void => {
                  const value = e.target.value;
                  setWatchSearchItems({
                    ...watchSearchItems,
                    // 「すべて」は条件なしとして undefined に揃える
                    subPatternId: value === '' ? undefined : String(value),
                  });
                }}
                size="small"
                renderValue={(selected: string): ReactElement | undefined => {
                  if (selected === '') {
                    return <span style={{ color: gray60 }}>子パターンで絞り込む</span>;
                  }
                  if (selected === SUB_PATTERN_ID_BASE) {
                    return <span>{subPatternLabel.base}</span>;
                  }
                  const matched = subPatterns.find(v => v.subPatternId === selected);
                  return <span>{matched?.sub_pattern_name ?? selected}</span>;
                }}
              >
                <MenuItem value="">{subPatternLabel.filterAll}</MenuItem>
                <MenuItem value={SUB_PATTERN_ID_BASE}>{subPatternLabel.base}</MenuItem>
                {subPatterns.map(variant => (
                  <MenuItem key={variant.subPatternId} value={variant.subPatternId}>
                    {variant.sub_pattern_name}
                  </MenuItem>
                ))}
              </Select>
            )}

            {/* ファイル名検索 */}
            {isFieldVisible('files.file_name') && (
              <TextField
                value={watchSearchItems.fileName ?? ''}
                sx={styles.formWidth}
                onChange={(e): void =>
                  setWatchSearchItems({ ...watchSearchItems, fileName: e.target.value || null })
                }
                placeholder="ファイル名で検索"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
                size="small"
              />
            )}

            {/* 日付範囲（デフォルト検索軸の最後に配置） */}
            {isFieldVisible('date_range') && (
              <Box sx={styles.datePickerRootBox}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={styles.datePickerBox}>
                    <DatePicker
                      sx={styles.datePickerWidth}
                      value={watchSearchItems.startDate}
                      format="YYYY/MM/DD"
                      onChange={(newValue): void => {
                        if (newValue && newValue.isValid()) {
                          setWatchSearchItems({ ...watchSearchItems, startDate: newValue });
                        }
                      }}
                      onAccept={(newValue): void => {
                        if (newValue && newValue.isValid()) {
                          setWatchSearchItems({ ...watchSearchItems, startDate: newValue });
                        }
                      }}
                      slots={{
                        textField: params => (
                          <TextField
                            {...params}
                            placeholder={dayjs().startOf('month').format('YYYY/MM/DD')}
                            size="small"
                            error={!!dateError}
                          />
                        ),
                      }}
                    />
                    <Typography>〜</Typography>
                    <DatePicker
                      sx={styles.datePickerWidth}
                      value={watchSearchItems.endDate}
                      format="YYYY/MM/DD"
                      onChange={(newValue): void => {
                        if (newValue && newValue.isValid()) {
                          setWatchSearchItems({ ...watchSearchItems, endDate: newValue });
                        }
                      }}
                      onAccept={(newValue): void => {
                        if (newValue && newValue.isValid()) {
                          setWatchSearchItems({ ...watchSearchItems, endDate: newValue });
                        }
                      }}
                      slots={{
                        textField: params => (
                          <TextField
                            {...params}
                            placeholder={dayjs().format('YYYY/MM/DD')}
                            size="small"
                            error={!!dateError}
                          />
                        ),
                      }}
                    />
                  </Box>
                  {dateError && (
                    <Typography sx={{ fontSize: '12px', color: red50, marginLeft: 1 }}>
                      {dateError}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* OCR読取項目の検索フィールド */}
            {enabledOcrFields.map(field => {
              // 日付型 + 完全一致モードのみ Datepicker。あいまいモードは TextField で扱う
              // （OCR が "3月25日" のような年なし生文字列で保存しているケースに対応するため）
              const isDatepicker =
                field.inputType === searchFieldInputType.date &&
                field.matchMode === searchMatchMode.exact;
              if (isDatepicker) {
                // OCR結果は item_list の outputFormat で文字列化されているため、
                // 検索キーワードも同フォーマットの文字列で揃える
                const dateFormat = field.outputFormat || DEFAULT_DATE_FORMAT;
                const rawValue = toKeywordText(ocrItemKeywords?.[field.fieldName]);
                const parsed = rawValue ? dayjs(rawValue, dateFormat) : null;
                const dateValue: Dayjs | null = parsed && parsed.isValid() ? parsed : null;
                return (
                  <DatePicker
                    key={field.fieldName}
                    sx={styles.formWidth}
                    value={dateValue}
                    format={dateFormat}
                    onChange={(newValue): void => {
                      if (!newValue) {
                        onOcrItemKeywordChange?.(field.fieldName, '');
                        return;
                      }
                      if (newValue.isValid()) {
                        onOcrItemKeywordChange?.(field.fieldName, newValue.format(dateFormat));
                      }
                    }}
                    onAccept={(newValue): void => {
                      if (!newValue) {
                        onOcrItemKeywordChange?.(field.fieldName, '');
                        return;
                      }
                      if (newValue.isValid()) {
                        onOcrItemKeywordChange?.(field.fieldName, newValue.format(dateFormat));
                      }
                    }}
                    slots={{
                      textField: params => (
                        <TextField {...params} placeholder={`${field.label}で検索`} size="small" />
                      ),
                    }}
                  />
                );
              }
              // 選択肢項目（ラジオボタン・プルダウン）: 設定済みの選択肢から複数選択する（#3204）
              // 選択肢が未設定の項目は inputType が text になるためここには来ない
              // （useSearchCustomize の toFieldInputType 参照）
              if (field.inputType === searchFieldInputType.choice && field.choices?.length) {
                const choices = field.choices;
                const selectedValues = toKeywordList(ocrItemKeywords?.[field.fieldName]);
                // 検索値は result_data に保存される出力値（value）。画面には表示名（label）を出す
                const labelByValue = new Map(
                  choices.map(choice => [resolveChoiceValue(choice), choice.label]),
                );
                return (
                  <Select
                    key={field.fieldName}
                    multiple
                    displayEmpty
                    sx={styles.selectFiled}
                    value={selectedValues}
                    onChange={(e): void => {
                      const value = e.target.value;
                      onOcrItemKeywordChange?.(
                        field.fieldName,
                        typeof value === 'string' ? value.split(',') : value,
                      );
                    }}
                    size="small"
                    renderValue={(selected: string[]): ReactElement => {
                      if (selected.length === 0) {
                        return <span style={{ color: gray60 }}>{`${field.label}で検索`}</span>;
                      }
                      // 選択肢から削除された値も検索条件としては残すため、出力値をそのまま表示する
                      return <span>{selected.map(v => labelByValue.get(v) ?? v).join('、')}</span>;
                    }}
                  >
                    {choices.map(choice => {
                      const choiceValue = resolveChoiceValue(choice);
                      return (
                        <MenuItem key={choiceValue} value={choiceValue}>
                          <Checkbox
                            checked={selectedValues.includes(choiceValue)}
                            size="small"
                            sx={{ '&.Mui-checked': { color: gray80 }, padding: '4px' }}
                          />
                          <ListItemText primary={choice.label} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                );
              }
              return (
                <TextField
                  key={field.fieldName}
                  value={toKeywordText(ocrItemKeywords?.[field.fieldName])}
                  sx={styles.formWidth}
                  onChange={(e): void => {
                    onOcrItemKeywordChange?.(field.fieldName, e.target.value);
                  }}
                  placeholder={`${field.label}で検索`}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                  size="small"
                />
              );
            })}

            <Box sx={{ display: 'flex', gap: 1, alignSelf: 'flex-end', marginLeft: 'auto' }}>
              <Button
                onClick={(): void => {
                  if (onClearSearch) {
                    onClearSearch();
                  }
                }}
                sx={styles.clearButton}
              >
                <Typography sx={{ fontSize: '14px' }}>検索クリア</Typography>
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AiOcrSearchForm;
